import Loading from '@components/Loading';
import SolidModal from '@components/Modals/SolidModal';
import { useCameraOn } from '@hooks/device/useCameraOn';
import { useGetPath } from '@hooks/interview/useGetPath';
import { useInterviewSession } from '@hooks/interview/useInterviewSession';
import { useReferrerAPI } from '@hooks/referrer/useReferrerAPI';
import { useAttendee } from '@hooks/utils/useAttendee';
import { useRoomCameraState } from '@hooks/utils/useRoomCameraState';
import { useSocket } from '@hooks/utils/useSocket';
import { useLocalVideo } from '@providers/LocalVideoProvider';
import { checkLogInterviewer } from '@stores/atoms/checkLog';
import { useSetAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { SocketChannelKeys, SocketNamespaceKeys } from 'server/constants/key';
import { MeetingStatus } from 'server/interfaces/meeting.enum';
import WaitingHeader from './Header';
import { Container, Content, StartMeeting } from './style';

const InterviewerWaitingPage = () => {
  const intervalRef = useRef<null | ReturnType<typeof setInterval>>(null);
  const navigate = useNavigate();
  const { rootPath } = useGetPath();
  const { isVideoEnabled } = useLocalVideo();
  const { getRoutePath } = useRoomCameraState();

  const { getInterviewInfo } = useInterviewSession();
  const { certificationCode, apiUrl, intervieweeName } = getInterviewInfo();

  const { fetchInterviewerLog, fetchInterviewerCreateLog } = useReferrerAPI();
  const socketClient = useSocket(SocketNamespaceKeys.INTERVIEWER);

  const [step, setStep] = useState(0);
  const [openStartMeeting, setOpenStartMeeting] = useState(false);

  const setAlreadyEnteredInterviewer = useSetAtom(checkLogInterviewer);

  const externalApiAddr = `https://${apiUrl}`;

  const moveNextStep = (next: number) => {
    if (next !== step) {
      setStep(next);
    }
  };

  const handleClickStartMeeting = () => {
    setOpenStartMeeting(true);
  };

  const handleClickConfirm = async () => {
    const createLog = await fetchInterviewerCreateLog({
      companyUrl: apiUrl,
      intervieweeName,
      certificationCode,
      apiUrl: externalApiAddr,
    });

    if (step === 2 && createLog.data) {
      moveNextStep(3);
      setOpenStartMeeting(false);

      socketClient.emit(
        SocketChannelKeys.INIT,
        JSON.stringify({ certificationCode, status: MeetingStatus.ROOM_OPEN }),
      );
    }
  };

  const handleClickCancel = () => {
    setOpenStartMeeting(false);
  };

  const isAlreadyEntered = async () => {
    const checkLog = await fetchInterviewerLog({
      companyUrl: apiUrl,
      name: intervieweeName,
      certificationCode,
      apiUrl: externalApiAddr,
    });
    setAlreadyEnteredInterviewer(checkLog.data);

    return checkLog;
  };

  const clearRefInterval = () => {
    if (intervalRef.current && typeof intervalRef.current === 'number') {
      clearInterval(intervalRef.current);
    }
  };

  const goToRoomOrWait = async () => {
    socketClient.on(SocketChannelKeys.CONNECTION, async () => {
      console.log('socket connected...');
    });

    const { data } = await isAlreadyEntered();

    if (data) {
      clearRefInterval();
      navigate(`${rootPath.ROOM}?camera=on`);
    } else {
      moveNextStep(1);

      socketClient.emit(
        SocketChannelKeys.INIT,
        JSON.stringify({
          certificationCode,
          status: MeetingStatus.INTERVIEWER_READY,
        }),
      );

      socketClient.on(certificationCode, (data) => {
        const { status } = JSON.parse(data);
        // console.log('================================');
        // console.log(status);
        // console.log('================================');

        switch (status) {
          case MeetingStatus.INTERVIEWEE_READY:
            return moveNextStep(2);
          case MeetingStatus.ROOM_OPEN:
            return moveNextStep(3);
          case MeetingStatus.START: {
            socketClient.close();
            return navigate(getRoutePath());
          }
          default:
            return;
        }
      });
    }
  };

  useAttendee();
  useCameraOn();

  console.log('isVideoEnabled:', isVideoEnabled);
  console.log(getRoutePath());

  useEffect(() => {
    goToRoomOrWait();

    intervalRef.current = setInterval(() => {
      goToRoomOrWait();
    }, 15000);

    return () => {
      () => {
        socketClient.emit(
          SocketChannelKeys.END,
          JSON.stringify({ certificationCode, status: MeetingStatus.END }),
        );

        clearRefInterval();
        setTimeout(() => {
          socketClient.close();
        }, 300);
      };
    };
  }, []);

  return (
    <Container>
      {/* 룸과 뷰어를 맞추기 위한 hotfix용 임시 코드 */}
      <WaitingHeader />
      <Content>
        {step === 1 && (
          <Loading text={`지원자가 아직\n대기실에 입장하지 않았습니다.`} />
        )}
        {step === 2 && (
          <>
            <Loading
              text={`지원자가 대기실에 입장하여 면접을 준비하고 있습니다.\n면접관 전원이 준비 완료되면 면접 시작 버튼을 눌러주세요.`}
            />
            <StartMeeting onClick={handleClickStartMeeting}>
              면접 시작
            </StartMeeting>
            {openStartMeeting && (
              <SolidModal
                onClickConfirm={handleClickConfirm}
                onClickCancel={handleClickCancel}
                text={`면접관 중 한명이 '면접 시작하기'를 누르면
          바로 면접이 시작됩니다.
          
          면접관 전원이 준비되었다면 '면접 시작하기'를
          아니라면 '취소'를 눌러주세요.`}
              />
            )}
          </>
        )}
        {step === 3 && (
          <Loading
            text={`곧 면접이 시작됩니다. (마이크, 카메라, 스피커를 모두 켠 상태로 유지해주세요.)`}
          />
        )}
      </Content>
    </Container>
  );
};

export default InterviewerWaitingPage;
